import { z } from "zod";

// ── Line schema ───────────────────────────────────────────────────────────────

export const JournalEntryLineSchema = z.object({
  id: z.string(),
  journalEntryId: z.string(),
  accountId: z.string().trim().min(1),
  debit: z.number().nullable(),
  credit: z.number().nullable(),
  description: z.string().optional(),
});

export type JournalEntryLine = z.infer<typeof JournalEntryLineSchema>;

export const CreateJournalEntryLineSchema = z.object({
  accountId: z.string().trim().min(1),
  debit: z.number().nonnegative().nullable(),
  credit: z.number().nonnegative().nullable(),
  description: z.string().optional(),
});

export type CreateJournalEntryLineInput = z.infer<
  typeof CreateJournalEntryLineSchema
>;

// ── Domain schema (DB / server truth) ─────────────────────────────────────────

export const JournalEntrySchema = z.object({
  id: z.string(),
  businessId: z.string(),
  date: z.union([z.string(), z.date()]),
  description: z.string(),
  reference: z.string().optional(),
  source: z.enum(["manual", "bank"]),
  classId: z.string().optional(),
  lines: z.array(JournalEntryLineSchema),
  documentIds: z.array(z.string()),
  createdDate: z.union([z.string(), z.date()]),
  lastModified: z.union([z.string(), z.date()]),
});

export type JournalEntry = z.infer<typeof JournalEntrySchema>;

function validateBalancedLines(
  lines: CreateJournalEntryLineInput[],
  ctx: z.RefinementCtx,
) {
  if (lines.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one line is required.",
      path: ["lines"],
    });
    return;
  }
  const debits = lines.reduce((s, e) => s + (e.debit ?? 0), 0);
  const credits = lines.reduce((s, e) => s + (e.credit ?? 0), 0);
  // Round to 4 decimal places to avoid floating point drift
  if (Math.abs(debits - credits) > 0.0001) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Lines are not balanced: debits ${debits.toFixed(4)} ≠ credits ${credits.toFixed(4)}.`,
      path: ["lines"],
    });
  }
}

export const CreateJournalEntrySchema = z
  .object({
    date: z.union([z.string(), z.date()]),
    description: z.string().trim().min(1, "Description is required"),
    reference: z.string().trim().optional(),
    classId: z.string().trim().optional(),
    /** When provided, links the journal entry to the vendor (vendor ↔ journal entry association). */
    vendorId: z.string().trim().optional(),
    lines: z.array(CreateJournalEntryLineSchema).min(1),
    documentIds: z.array(z.string()).default([]),
  })
  .superRefine((val, ctx) => validateBalancedLines(val.lines, ctx));

export const UpdateJournalEntryHeaderSchema = z.object({
  date: z.union([z.string(), z.date()]).optional(),
  description: z.string().trim().min(1).optional(),
  reference: z.string().trim().optional(),
  classId: z.string().trim().optional(),
  documentIds: z.array(z.string()).optional(),
});

export const UpdateJournalEntrySchema = z
  .object({
    date: z.union([z.string(), z.date()]).optional(),
    description: z.string().trim().min(1).optional(),
    reference: z.string().trim().optional(),
    classId: z.string().trim().optional(),
    lines: z.array(CreateJournalEntryLineSchema).min(1).optional(),
    documentIds: z.array(z.string()).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.lines !== undefined) {
      validateBalancedLines(val.lines, ctx);
    }
  });

export type CreateJournalEntryInput = z.infer<typeof CreateJournalEntrySchema>;
export type UpdateJournalEntryInput = z.infer<typeof UpdateJournalEntrySchema>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const JournalEntryLineFormSchema = z.object({
  /** Stable client-side key used to keep rows stable across re-renders. */
  key: z.string().optional(),
  accountId: z.string(),
  debit: z.string(),
  credit: z.string(),
  description: z.string().optional(),
});

export type JournalEntryLineForm = z.infer<typeof JournalEntryLineFormSchema>;

export const JournalEntryFormSchema = z.object({
  date: z.string().min(1, "Date is required"),
  description: z.string().min(1, "Description is required"),
  reference: z.string().optional(),
  classId: z.string().optional(),
  lines: z.array(JournalEntryLineFormSchema).min(1),
  documentIds: z.array(z.string()).optional(),
});

export type JournalEntryForm = z.infer<typeof JournalEntryFormSchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Convert a user-entered amount string to a number, stripping locale-specific
 * formatting like thousands separators (commas) so "1,620" → 1620.
 * Returns `null` for empty strings, `NaN` for truly unparseable input.
 */
export function parseAmount(value: string): number | null {
  if (value === "") return null;
  const cleaned = value.replace(/,/g, "");
  return Number(cleaned);
}

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

export function formToDomain(input: JournalEntryForm): CreateJournalEntryInput {
  return {
    date: input.date,
    description: input.description,
    reference: input.reference || undefined,
    classId: input.classId || undefined,
    lines: input.lines.map((e) => ({
      accountId: e.accountId,
      debit: e.debit !== "" ? parseAmount(e.debit) : null,
      credit: e.credit !== "" ? parseAmount(e.credit) : null,
      description: e.description,
    })),
    documentIds: input.documentIds ?? [],
  };
}

export function domainToForm(item: JournalEntry): JournalEntryForm {
  const d = item.date instanceof Date ? item.date : new Date(item.date);
  const dateStr = d.toISOString().split("T")[0]; // YYYY-MM-DD
  return {
    date: dateStr,
    description: item.description,
    reference: item.reference,
    classId: item.classId,
    lines: item.lines.map((e) => ({
      key: `line-${e.id}`,
      accountId: e.accountId,
      debit: e.debit != null ? String(e.debit) : "",
      credit: e.credit != null ? String(e.credit) : "",
      description: e.description,
    })),
    documentIds: item.documentIds,
  };
}
